import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, MapPin, Loader2, CheckCircle, MessageSquare, Clock, Sparkles, ExternalLink } from 'lucide-react';
import { personalInfo } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import MouseFollower from '../components/MouseFollower';

const BACKEND_URL = '';

const InstagramIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: size, height: size }}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const ContactPage = () => {
  const { themeColor, currentButtonShape } = useTheme();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch {
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: InstagramIcon,
      title: 'Instagram',
      value: '@canervac',
      href: 'https://instagram.com/canervac',
      description: 'DM ile ulaşabilirsiniz',
      primary: true,
    },
    {
      icon: Mail,
      title: 'E-posta',
      value: personalInfo.email,
      href: `mailto:${personalInfo.email}`,
      description: '24 saat içinde yanıt',
      primary: false,
    },
    {
      icon: MapPin,
      title: 'Konum',
      value: personalInfo.location,
      href: null,
      description: 'Uzaktan çalışıyorum',
      primary: false,
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      <MouseFollower />

      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 w-full h-1/2 opacity-5 pointer-events-none"
        style={{ background: `radial-gradient(circle at top, ${themeColor.primary}, transparent 70%)` }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
            style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)`, color: themeColor.primary }}
          >
            <MessageSquare size={16} />
            İletişim
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">Benimle İletişime Geç</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Proje fikirleriniz, iş birliği önerileriniz veya sadece merhaba demek için benimle iletişime geçebilirsiniz.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Contact Methods */}
          <motion.div
            className="lg:col-span-2 space-y-4"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            {contactMethods.map((method) => (
              <motion.div
                key={method.title}
                className={`p-5 rounded-3xl transition-all ${method.href ? 'cursor-pointer' : ''} ${
                  method.primary
                    ? 'glass-strong glass-hover gradient-border'
                    : 'glass glass-hover'
                }`}
                onClick={() => method.href && window.open(method.href, method.href.startsWith('mailto') ? '_self' : '_blank')}
                whileHover={{ y: -3, scale: 1.01 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`contact-method-${method.title.toLowerCase()}`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)` }}
                  >
                    {typeof method.icon === 'function' && method.icon.name === 'InstagramIcon' ? (
                      <span style={{ color: themeColor.primary }}><method.icon size={24} /></span>
                    ) : (
                      <method.icon size={24} style={{ color: themeColor.primary }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-zinc-500 mb-0.5">{method.title}</p>
                      {method.primary && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded-full font-medium" style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.15)`, color: themeColor.primary }}>
                          Ana
                        </span>
                      )}
                    </div>
                    <p className="text-white font-medium">{method.value}</p>
                    <p className="text-xs text-zinc-600">{method.description}</p>
                  </div>
                  {method.href && <ExternalLink size={16} className="text-zinc-600" />}
                </div>
              </motion.div>
            ))}

            {/* Availability */}
            <motion.div
              className="p-6 rounded-3xl glass gradient-border"
              style={{
                background: `linear-gradient(135deg, rgba(${themeColor.rgb}, 0.08), rgba(255,255,255,0.02))`,
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-3">
                <motion.div
                  className="w-3 h-3 rounded-full bg-green-500"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="text-white font-semibold">İş İçin Uygunum</span>
                <Sparkles size={16} style={{ color: themeColor.primary }} />
              </div>
              <p className="text-zinc-400 text-sm mb-3">
                Yeni projeler ve iş birliği fırsatları için açığım. Freelance veya part-time çalışmalar için iletişime geçin!
              </p>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Clock size={14} />
                <span>Genellikle 24 saat içinde yanıt veriyorum</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="relative">
              <div
                className="absolute -inset-4 rounded-3xl opacity-10 blur-2xl pointer-events-none"
                style={{ backgroundColor: themeColor.primary }}
              />
              <form
                onSubmit={handleSubmit}
                className="relative p-8 rounded-3xl glass-strong gradient-border"
              >
                <h3 className="text-xl font-bold text-white mb-6">Mesaj Gönder</h3>
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Adınız *</label>
                      <input
                        type="text" name="name" value={formData.name} onChange={handleChange} required
                        className="w-full px-4 py-3.5 bg-zinc-800/50 border border-zinc-700/50 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-all"
                        style={{ borderRadius: currentButtonShape.radius }}
                        placeholder="Adınızı girin"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">E-posta *</label>
                      <input
                        type="email" name="email" value={formData.email} onChange={handleChange} required
                        className="w-full px-4 py-3.5 bg-zinc-800/50 border border-zinc-700/50 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-all"
                        style={{ borderRadius: currentButtonShape.radius }}
                        placeholder="E-posta adresiniz"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Mesajınız *</label>
                    <textarea
                      name="message" value={formData.message} onChange={handleChange} required rows={6}
                      className="w-full px-4 py-3.5 bg-zinc-800/50 border border-zinc-700/50 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-all resize-none"
                      style={{ borderRadius: currentButtonShape.radius }}
                      placeholder="Mesajınızı yazın..."
                      data-testid="contact-input-message"
                    />
                  </div>
                  {error && (
                    <motion.p className="text-red-400 text-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{error}</motion.p>
                  )}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting || isSubmitted}
                    className="w-full py-4 font-medium text-black transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 btn-glow"
                    style={{ backgroundColor: themeColor.primary, borderRadius: currentButtonShape.radius }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="animate-spin" size={20} />Gönderiliyor...</>
                    ) : isSubmitted ? (
                      <><CheckCircle size={20} />Mesaj Gönderildi!</>
                    ) : (
                      <><Send size={20} />Mesaj Gönder</>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
